export const en = {
  nav: {
    primary: 'Primary',
    inbox: 'Inbox',
    knowledge: 'Knowledge',
    projects: 'Projects',
    users: 'Users',
    config: 'Config',
    aiUsage: 'AI Usage',
    health: 'Health',
    submit: 'Submit',
    myTickets: 'My Tickets',
    profile: 'Profile',
    switchTenant: 'Switch tenant',
    signOut: 'Sign out',
    role: {
      admin: 'Admin',
      expert: 'Expert',
    },
    widgets: {
      ticketsThisMonth: 'Tickets this month',
      lastMonth: '{count} last month',
      weekStreak: 'Week streak',
      resolved: 'Resolved',
      kbArticles: 'KB articles',
      responsesHelped: 'Responses helped',
      responsesHelpedTitle: 'Your KB entries were used in drafts for {count} tickets that received a response',
    },
  },
  roles: {
    line_manager: 'Line manager',
    expert: 'Expert',
    admin: 'Admin',
    system: 'System',
  },
  priority: {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  },
  ticket: {
    status: {
      DRAFT: 'Draft',
      SUBMITTED: 'Submitted - analyzing...',
      NEEDS_INFO: 'More information needed',
      TRIAGED: 'Triaged - awaiting expert',
      AWAITING_EXPERT: 'Ready for review',
      EXPERT_REVIEWING: 'In expert review',
      RESPONSE_DRAFTING: 'Preparing response...',
      RESPONSE_READY: 'Response ready',
      RESPONDED: 'Expert has responded',
      AWAITING_REPLY: 'Awaiting your reply',
      CLOSED: 'Closed',
      ARCHIVED: 'Archived',
    },
  },
  settings: {
    profileTitle: 'Profile',
    personalInfo: 'Personal info',
    firstName: 'First name',
    lastName: 'Last name',
    timeZone: 'Time zone',
    language: {
      label: 'Preferred language',
    },
    saved: 'Settings updated successfully.',
    save: 'Save settings',
    saving: 'Saving...',
    error: 'Failed to save. Please try again.',
    activity: 'Activity',
    streak: '{count}-week streak',
    actions: '{count} actions',
    stats: 'Stats',
    ticketsThisMonth: 'Tickets this month',
    currentStreak: 'Current streak',
    totalResolved: 'Total resolved',
    activeKbArticles: 'Active KB articles',
    totalResponsesHelped: 'Total responses helped by your KB',
    calendar: {
      ticketsSubmitted: 'tickets submitted',
      expertActions: 'expert actions',
      mon: 'Mon',
      wed: 'Wed',
      fri: 'Fri',
    },
  },
  loginConfirm: {
    heading: 'Sign in to',
    body: 'Click the button below to complete sign-in for {email}.',
    cta: 'Complete sign-in',
    back: '← Use a different email',
  },
  onboarding: {
    title: 'One last thing',
    body: 'Tell us your name so experts and line managers can recognize you.',
    continue: 'Continue',
    saving: 'Saving...',
    error: 'Something went wrong. Please try again.',
  },
  submit: {
    title: 'Submit an Issue',
    intro: 'Complete all fields. An expert will respond as soon as possible.',
    project: 'Project',
    projectHelp: 'If your project is not listed, please contact an expert or admin to add the project you need.',
    noProjects: 'No active projects available',
    selectProject: 'Select project...',
    projectRequired: 'Please select a project.',
    department: 'Department',
    selectDepartment: 'Select department...',
    departmentRequired: 'Please select a department.',
    dateOfOccurrence: 'Date of Occurrence',
    dateFuture: 'Date of Occurrence cannot be in the future. Choose today or an earlier date.',
    dateRequired: 'Date of Occurrence is required.',
    issueCategory: 'Issue Category',
    selectCategory: 'Select category...',
    categoryRequired: 'Please select an issue category.',
    affectedSystem: 'Affected System / Process',
    selectSystem: 'Select system...',
    systemRequired: 'Please select an affected system.',
    priority: 'Priority',
    selectPriority: 'Select priority...',
    priorityRequired: 'Please select a priority.',
    actionRequested: 'Action Requested',
    selectAction: 'Select action...',
    actionRequired: 'Please select an action.',
    description: 'Problem Description',
    descriptionPlaceholder: 'Describe the issue in as much detail as possible...',
    descriptionRequired: 'A description is required.',
    successCriteria: 'What does success look like?',
    successCriteriaPlaceholder: 'Describe the outcome that would resolve this issue...',
    successCriteriaRequired: 'Please describe what success looks like.',
    charactersOver: '{count} characters over limit',
    charactersRemaining: '{count} characters remaining',
    submitIssue: 'Submit issue',
    submitting: 'Submitting...',
    error: 'Something went wrong. Please try again.',
    ticketSubmitted: 'Ticket submitted',
    ticketInQueue: '{referenceNumber} is now in the queue.',
    evaluating: 'Evaluating your issue...',
    moreDetails: 'A few more details needed',
    answerPlaceholder: 'Your answer...',
    submitAdditional: 'Submit additional information',
    viewTicket: 'View ticket',
    promptText: 'Describe the issue in detail, including what you observed and when it started.',
    waitingBody: "We're checking whether we need any additional information before routing your issue to an expert. This usually takes a few seconds — please wait.",
    needsInfoBody: 'To help our expert respond as quickly as possible, please answer the questions below.',
    completeBody: "Your issue is now in the queue and an expert will review it shortly. You'll receive an email when they respond.",
  },
  inbox: {
    title: 'Expert Inbox',
    shown: '{count} shown',
    project: 'Project',
    allProjects: 'All projects',
    category: 'Category',
    allCategories: 'All categories',
    priority: 'Priority',
    priorityHelp: 'Priority is selected by the line manager at submission time. Sorting by priority puts critical and high-priority tickets first.',
    allPriorities: 'All priorities',
    status: 'Status',
    statusHelp: 'Status shows where the ticket is in the workflow, from AI triage through expert review, response, follow-up, and close.',
    allUnresponded: 'All unresponded',
    allStatuses: 'All statuses',
    sort: 'Sort',
    sortHelp: 'Choose whether the inbox is ordered by when the ticket was submitted, when the issue happened, or the priority reported by the line manager.',
    dateSubmitted: 'Date submitted',
    dateOfOccurrence: 'Date of occurrence',
    userPriority: 'User priority',
    applyFilters: 'Apply filters',
    reset: 'Reset',
    clearTitle: 'Inbox clear',
    clearBody: 'Tickets will appear as soon as summarization finishes.',
    statusLabels: {
      SUBMITTED: 'Awaiting AI triage',
      NEEDS_INFO: 'Needs info',
      TRIAGED: 'Triaged',
      AWAITING_EXPERT: 'Ready for review',
      EXPERT_REVIEWING: 'In expert review',
      RESPONSE_DRAFTING: 'Drafting',
      RESPONSE_READY: 'Response ready',
      RESPONDED: 'Responded',
      CLOSED: 'Closed',
    },
  },
  tickets: {
    title: 'My Tickets',
    intro: 'Track the issues you have submitted to the expert team.',
    newTicket: 'New ticket',
    emptyTitle: 'No tickets yet',
    emptyBody: 'Submitted issues will appear here.',
    issueSubmitted: 'Issue submitted',
    occurred: 'Occurred {date}',
    updated: 'Updated {date}',
  },
  ticketDetail: {
    issueSubmitted: 'Issue submitted',
    degradedAi: 'AI processing is unavailable. An expert will review your ticket manually.',
    aiSummary: 'AI Summary',
    project: 'Project',
    dateOfOccurrence: 'Date of occurrence',
    department: 'Department',
    issueCategory: 'Issue category',
    affectedSystem: 'Affected system',
    declaredPriority: 'Declared priority',
    actionRequested: 'Action requested',
    successCriteria: 'Success criteria',
    waitingForLineManager: 'Waiting for line manager input',
    waitingForLineManagerBody: 'The AI requested additional information from the line manager before this ticket is routed for an expert response.',
    expertOverrideResponse: 'Expert override response',
    expertOverrideBody: 'You can respond now, even if AI analysis, draft generation, or clarification is still in progress.',
    responseDraft: 'Response draft',
    responseDraftBody: 'Generate a KB-grounded draft response for expert review.',
    generatingDraft: 'Generating response draft... this usually takes a few seconds.',
    kbSources: 'KB Sources',
    regenerateDraft: 'Regenerate draft',
    noKbSources: 'No KB sources were used.',
    knowledgeGaps: 'Knowledge gaps',
    clarificationForm: {
      heading: 'To help our expert respond quickly, please answer the following:',
      submitButton: 'Submit additional information',
      submitting: 'Submitting…',
      minWords: 'Please write at least a few words.',
      error: 'Something went wrong. Please try again.',
    },
    closeButton: {
      label: 'Close ticket',
      closing: 'Closing…',
      error: 'Failed to close ticket',
      generic: 'Something went wrong',
      confirmTitle: 'Close this ticket?',
      confirmBody: 'Once closed, the ticket will no longer be active. You can still view it in the archive.',
      confirmCancel: 'Cancel',
      confirmClose: 'Close ticket',
    },
    expertResponseForm: {
      responseLabel: 'Expert Response',
      severity: 'Severity',
      severityTooltip: 'Classify the operational impact of the issue after review. This metadata helps reporting and future knowledge-base recommendations.',
      selectSeverity: 'Select severity…',
      concernType: 'Concern type',
      concernTooltip: 'Capture the main reason this issue needed expert support, such as a process gap, knowledge gap, or unclear guidance.',
      selectConcern: 'Select concern…',
      confirmAiDraftTitle: 'Send draft unchanged?',
      confirmAiDraft: 'You are about to send the AI draft unchanged. Confirm that you reviewed it and approve sending it as the expert response.',
      confirmAiDraftSend: 'Confirmed, send',
      confirmAiDraftCancel: 'Wait, do not send',
      send: 'Send Response',
      sending: 'Sending...',
      error: 'Unable to send response',
      translationPreviewHeading: 'Translation for submitter (Spanish)',
      translationPreviewNote: 'This submitter prefers Spanish. Review the AI translation below before sending.',
      translationGenerating: 'Generating translation…',
      translationError: 'Failed to generate translation — you can enter it manually.',
      translationStaleWarning: 'Response text changed. Update the translation before sending.',
      regenerateTranslation: 'Regenerate',
      sendWithTranslation: 'Review Translation & Send',
    },
    followUpForm: {
      awaitingReplyHeading: 'Reply to the expert',
      awaitingReplyBody: 'The expert has asked for more information. Please reply below and your ticket will be re-reviewed.',
      awaitingReplyPlaceholder: 'Add your follow-up information here…',
      awaitingReplyButton: 'Send reply',
      followUpHeading: 'Follow up with the expert',
      followUpBody: 'The expert has responded to your ticket. Use the form below if you have further questions or comments.',
      followUpPlaceholder: 'Write your follow-up here…',
      followUpButton: 'Send follow-up',
      sending: 'Sending…',
      replyError: 'Failed to send reply',
      generic: 'Something went wrong',
      minWords: 'Please write at least a few words.',
    },
    expertReplyForm: {
      heading: 'Reply to line manager',
      body: 'The line manager has replied to your response. Send a follow-up message below.',
      placeholder: 'Write your follow-up here…',
      send: 'Send reply',
      sending: 'Sending…',
      error: 'Failed to send reply',
      generic: 'Something went wrong',
      minWords: 'Please write at least a few words.',
    },
    draftButton: {
      generate: 'Generate response',
      retryDraft: 'Retry draft',
      generating: 'Generating…',
      error: 'Unable to request draft',
    },
    tagEditor: {
      title: 'Tags',
      tooltip: 'Tags help experts organize tickets, find related work later, and improve filtering and reporting.',
      hint: 'Press Enter or comma to add. Up to 10 tags.',
      placeholder: 'Add tags…',
      saving: 'Saving…',
      error: 'Failed to save tags',
      networkError: 'Network error — please try again',
    },
  },
  adminUsers: {
    title: 'Users',
    intro: 'Manage team members. Invited users can sign in with their email via the magic-link or Google SSO.',
    inviteTitle: 'Invite a new member',
    email: 'Email',
    role: 'Role',
    language: 'Language',
    addUser: 'Add user',
    adding: 'Adding...',
    failedInvite: 'Failed to invite user',
    inviteSuccess: '{email} can now sign in. They have been sent an invite to their email.',
    activeMembers: 'Active members',
    countFiltered: '{filtered} of {total}',
    filterPlaceholder: 'Filter by name or email',
    disabled: 'Disabled',
    none: 'None',
    member: 'Member',
    lastLogin: 'Last login',
    actions: 'Actions',
    you: 'you',
    never: 'Never',
    disable: 'Disable',
    reEnable: 'Re-enable',
    actionFailed: 'Action failed',
    roleHelp: 'Line managers can submit and track their own tickets. Experts can review tickets and manage knowledge/projects. Admins can also manage users and tenant configuration.',
    activeHelp: 'Disabling a member blocks access for this tenant without deleting their user record or historical ticket activity.',
  },
  knowledge: {
    title: 'Knowledge Base',
    intro: 'Search, author, and review verified operational knowledge.',
    entries: '{count} entries',
    emptyTitle: 'No entries yet',
    emptyBody: 'Create the first verified markdown entry.',
    updated: 'Updated',
    queued: 'queued',
    editEntry: 'Edit Entry',
    createEntry: 'Create Entry',
    newEntry: 'New entry',
    form: {
      fieldTitle: 'Title',
      titlePlaceholder: 'Line 3 filler E42 fault',
      slug: 'Slug',
      slugTooltip: 'The slug becomes the stable URL-friendly identifier for this entry. Leave it blank to generate one from the title.',
      slugPlaceholder: 'auto-generated if blank',
      type: 'Type',
      typeTooltip: 'Choose the knowledge shape: concept for background, procedure for steps, entity for equipment or systems, log for observed events, and index for navigation.',
      tags: 'Tags',
      tagsTooltip: 'Tags help experts find related guidance and improve retrieval context for future AI drafts.',
      tagsHint: 'Press Enter or comma to add. Up to 10 tags.',
      tagsPlaceholder: 'Add tags...',
      markdown: 'Markdown',
      markdownTooltip: 'Replace the sample with verified, reusable guidance. This content can be retrieved for future AI-assisted draft responses.',
      preview: 'Preview',
      previewEmpty: 'Markdown preview appears here while you write.',
      saving: 'Saving...',
      update: 'Update entry',
      create: 'Create entry',
      sampleError: 'Replace the sample markdown with verified knowledge before creating an entry.',
      saveError: 'Unable to save knowledge entry',
    },
    search: {
      placeholder: 'Search procedures, symptoms, equipment...',
      searching: 'Searching...',
      search: 'Search',
    },
    proposals: {
      heading: 'Knowledge Review',
      intro: 'Expert responses created these pending KB updates.',
      accept: 'Accept',
      dismiss: 'Dismiss',
      error: 'Unable to review proposal',
    },
    translation: {
      heading: 'Spanish Translation',
      statusApproved: 'Approved',
      statusPending: 'Pending review',
      statusStale: 'Stale — source changed',
      noTranslation: 'No Spanish translation yet.',
      generate: 'Generate with AI',
      generating: 'Generating…',
      generateError: 'Failed to generate translation',
      approve: 'Approve',
      reject: 'Reject',
      approving: 'Approving…',
      rejecting: 'Rejecting…',
      reviewError: 'Unable to update translation',
      editTranslation: 'Edit translation',
      saveTranslation: 'Save translation',
      savingTranslation: 'Saving…',
      saveTranslationError: 'Unable to save translation',
      translatedBadge: 'Translated',
      staleBadge: 'Stale translation',
      canonicalNote: 'Editing here updates the Spanish translation only. The English canonical entry is unchanged.',
    },
  },
  adminConfig: {
    title: 'Tenant configuration',
    intro: 'Changes take effect immediately for new AI jobs. Existing in-flight jobs are not affected.',
    missing: 'Tenant config not found.',
    submissionUi: 'Submission UI',
    submissionPromptText: 'Submission prompt text',
    additionalInfoPromptText: 'Additional info prompt text',
    maxSubmissionChars: 'Max submission characters',
    maxCharsHint: '500–20 000',
    aiSettings: 'AI Settings',
    aiModel: 'AI model',
    aiModelTooltip: 'Controls which model handles completeness checks, summaries, drafts, and knowledge-base proposals for this tenant.',
    temperature: 'Temperature',
    temperatureHint: '0.0 (deterministic) – 1.0 (creative)',
    temperatureTooltip: 'Lower values make AI output more consistent. Higher values make it more varied, which can be useful for drafting but riskier for regulated guidance.',
    minInfoScore: 'Min info score threshold',
    minInfoScoreHint: '0.0–1.0 — tickets below this score trigger clarification',
    minInfoScoreTooltip: 'Raises or lowers how strict the completeness agent is before asking the line manager for more information.',
    maxClarificationCycles: 'Max clarification cycles',
    maxCyclesHint: '0–10',
    aiJobTimeout: 'AI job timeout (seconds)',
    timeoutHint: '30–600',
    dataCompliance: 'Data & Compliance',
    dataProcessingConsent: 'Data processing consent — allow ticket content to be sent to the AI provider',
    dataProcessingTooltip: 'When disabled, tenant ticket content should not be sent to the configured AI provider; experts can still handle tickets manually.',
    save: 'Save changes',
    saving: 'Saving…',
    saved: 'Saved successfully.',
    saveError: 'Failed to save config',
    networkError: 'Network error — please try again',
  },
  platformHealth: {
    title: 'Platform Health',
    intro: 'Tenant-scoped adoption, ticket flow, draft quality, and knowledge-base health.',
    adoption: 'Adoption',
    ticketFlowHealth: 'Ticket flow health',
    draftQuality: 'Draft quality',
    kbHealth: 'KB health',
    activeThisWeek: 'Active this week',
    activeThisMonth: 'Active this month',
    activationRate: 'Activation rate',
    activationRateDetail: 'Enabled users with at least one real action',
    ticketsPerActiveLm: 'Tickets / active LM',
    ticketsPerActiveLmDetail: 'Current calendar week',
    orphanedTickets: 'Orphaned tickets',
    orphanedTicketsDetail: 'No expert message in the last 48 hours',
    medianFirstAction: 'Median first action',
    medianFirstActionDetail: 'Submission to first expert response',
    followUpRate: 'Follow-up rate',
    followUpRateDetail: 'Responded tickets with line-manager follow-up',
    closedWithoutClarification: 'Closed without clarification',
    closedWithoutClarificationDetail: 'Proxy metric only',
    closedWithoutClarificationNote: 'Resolved without external channels is a proxy: it measures closed tickets that never entered NEEDS_INFO, not actual outside-channel behavior.',
    draftSendThrough: 'Draft send-through',
    draftSendThroughDetail: 'Accepted AI drafts',
    draftEditRate: 'Draft edit rate',
    draftEditRateDetail: 'Sent-ticket drafts marked edited',
    draftRegeneration: 'Draft regeneration',
    draftRegenerationDetail: 'Tickets with multiple draft artifacts',
    activeKbEntriesMetric: 'Active KB entries',
    activeKbEntriesMetricDetail: 'Not soft-deleted',
    usedInDrafts: 'Used in drafts',
    usedInDraftsDetail: 'Retrieved at least once',
    neverRetrieved: 'Never retrieved',
    neverRetrievedDetail: 'Review candidates',
    contributingEntries: 'Contributing entries',
    contributingEntriesDetail: 'Retrieved for responded tickets',
    week: 'Week',
    submissions: 'Submissions',
  },
  aiUsage: {
    title: 'AI Usage',
    intro: 'Current tenant AI call volume, latency, failures, and estimated spend.',
    from: 'From',
    to: 'To',
    refresh: 'Refresh',
    totalCalls: 'Total calls',
    successRate: 'Success rate',
    totalTokens: 'Total tokens',
    estimatedCost: 'Estimated cost',
    perAgentBreakdown: 'Per-agent breakdown',
    agent: 'Agent',
    calls: 'Calls',
    success: 'Success',
    p50Latency: 'P50 latency',
    p95Latency: 'P95 latency',
    cost: 'Cost',
    noCallsFound: 'No AI calls found for this date range.',
    recentErrors: 'Recent errors',
    time: 'Time',
    code: 'Code',
    message: 'Message',
    noErrorsFound: 'No failed AI calls found for this date range.',
  },
  projects: {
    title: 'Projects',
    intro: 'Manage the project list that line managers select from when submitting tickets.',
    projectNamePlaceholder: 'Project name',
    addProject: 'Add project',
    adding: 'Adding…',
    deactivate: 'Deactivate',
    addError: 'Failed to add project',
    noProjects: 'No active projects.',
    createdBy: 'Created by',
  },
  switchTenant: {
    title: 'Switch tenant',
    intro: 'You have access to multiple tenants. Select one to continue.',
    current: 'Current',
    switch: 'Switch',
    switching: 'Switching…',
    loading: 'Loading…',
  },
} as const

type WidenStrings<T> = {
  readonly [K in keyof T]: T[K] extends string ? string : WidenStrings<T[K]>
}

export type EnglishDictionary = WidenStrings<typeof en>
